import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add } from '@mui/icons-material';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, MessageShortcutData } from '../../api';
import { ApiError } from '../../api/core/ApiError';
import { useSafeExecute } from '../../common/apiNotifications/safeExecute';
import { mb } from '../../common/theme/mbTokens';
import {
  getShortcutLength,
  SHORTCUT_TEXT_MAX_LENGTH,
  SHORTCUT_TITLE_MAX_LENGTH,
  SortableShortcutCard,
} from './SortableShortcutCard';

const MESSAGE_SHORTCUTS_MAX = 50;

type MessageShortcutsSettingsProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (open: boolean) => void;
};

type MessageShortcutRow = {
  key: string;
  id?: string;
  title: string;
  text: string;
};

const rowsFromAccount = (account: AccountData): MessageShortcutRow[] =>
  (account?.extension_settings?.message_shortcuts ?? []).map((shortcut: MessageShortcutData) => ({
    key: shortcut.id ?? shortcut.title,
    id: shortcut.id,
    title: shortcut.title,
    text: shortcut.text,
  }));

export const MessageShortcutsSettings = ({
  account,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
}: MessageShortcutsSettingsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const safeExecute = useSafeExecute();
  const [rows, setRows] = useState<MessageShortcutRow[]>(() => rowsFromAccount(account));
  const [newRowCounter, setNewRowCounter] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const normalizedTitles = rows.map((row) => row.title.trim().toLocaleLowerCase());

  const getTitleError = (row: MessageShortcutRow): string | undefined => {
    const normalizedTitle = row.title.trim().toLocaleLowerCase();
    if (normalizedTitle === '') {
      return t('extensionSettings.shortcuts.titleRequired');
    }
    if (getShortcutLength(row.title) > SHORTCUT_TITLE_MAX_LENGTH) {
      return t('extensionSettings.shortcuts.titleTooLong');
    }
    if (normalizedTitles.filter((title) => title === normalizedTitle).length > 1) {
      return t('extensionSettings.shortcuts.titleDuplicate');
    }
    return undefined;
  };

  const getMessageError = (row: MessageShortcutRow): string | undefined => {
    if (row.text.trim() === '') {
      return t('extensionSettings.shortcuts.messageRequired');
    }
    if (getShortcutLength(row.text) > SHORTCUT_TEXT_MAX_LENGTH) {
      return t('extensionSettings.shortcuts.messageTooLong');
    }
    return undefined;
  };

  const savedRows = rowsFromAccount(account);
  const hasChanges =
    rows.length !== savedRows.length ||
    rows.some(
      (row, index) =>
        row.id !== savedRows[index].id || row.title !== savedRows[index].title || row.text !== savedRows[index].text,
    );
  const hasErrors = rows.some((row) => getTitleError(row) || getMessageError(row));
  const isLimitReached = rows.length >= MESSAGE_SHORTCUTS_MAX;

  const addRow = () => {
    setRows((current) => [...current, { key: `new-${newRowCounter}`, title: '', text: '' }]);
    setNewRowCounter((current) => current + 1);
  };

  const updateRow = (key: string, changes: Partial<MessageShortcutRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)));

  const deleteRow = (key: string) => setRows((current) => current.filter((row) => row.key !== key));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    setRows((current) =>
      arrayMove(
        current,
        current.findIndex((row) => row.key === active.id),
        current.findIndex((row) => row.key === over.id),
      ),
    );
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const updatedAccount: AccountData = await safeExecute({
        apiCall: () =>
          AccountService.updateMessageShortcuts({
            message_shortcuts: rows.map((row) => ({ id: row.id, title: row.title.trim(), text: row.text.trim() })),
          }),
        successMessage: t('extensionSettings.shortcuts.saved'),
        onErrorMessageGenerator: (error: ApiError) => {
          const detail = error.body?.detail;
          return typeof detail === 'string' ? detail : t('api.error.default');
        },
      });
      await updateLocalAccount(updatedAccount);
      setRows(rowsFromAccount(updatedAccount));
      setIsUpdatedSnackOpen(true);
    } catch {
      return;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" gap={2}>
      <Grid container flexDirection="column">
        <Grid container alignItems="center" gap={1}>
          <Typography variant="h6">{t('extensionSettings.shortcuts.title')}</Typography>
          <Chip size="small" label={`${rows.length}/${MESSAGE_SHORTCUTS_MAX}`} />
        </Grid>
        <Typography variant="caption" color="text.secondary">
          {t('extensionSettings.shortcuts.description')}
        </Typography>
      </Grid>
      {rows.length === 0 && (
        <Box
          sx={{
            border: '1px dashed',
            borderColor: mb.control,
            borderRadius: '8px',
            p: 4,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}>
          <Typography variant="subtitle2">{t('extensionSettings.shortcuts.emptyTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('extensionSettings.shortcuts.emptyBody')}
          </Typography>
          <Button variant="outlined" startIcon={<Add />} onClick={addRow}>
            {t('extensionSettings.shortcuts.add')}
          </Button>
        </Box>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={rows.map((row) => row.key)} strategy={verticalListSortingStrategy}>
          <Grid container flexDirection="column" flexWrap="nowrap" gap={1.5}>
            {rows.map((row) => (
              <SortableShortcutCard
                key={row.key}
                rowKey={row.key}
                title={row.title}
                text={row.text}
                titleError={getTitleError(row)}
                messageError={getMessageError(row)}
                onTitleChange={(title) => updateRow(row.key, { title })}
                onTextChange={(text) => updateRow(row.key, { text })}
                onDelete={() => deleteRow(row.key)}
              />
            ))}
          </Grid>
        </SortableContext>
      </DndContext>
      {rows.length >= 2 && (
        <Typography variant="caption" color="text.secondary">
          {t('extensionSettings.shortcuts.reorderHint')}
        </Typography>
      )}
      {isLimitReached && <Alert severity="info">{t('extensionSettings.shortcuts.limitReached')}</Alert>}
      <Grid
        container
        alignItems="center"
        flexWrap="nowrap"
        gap={1}
        sx={{
          position: { xs: 'sticky', md: 'static' },
          bottom: 0,
          py: { xs: 1.5, md: 0 },
          backgroundColor: { xs: 'background.paper', md: 'transparent' },
          borderTop: { xs: '1px solid', md: 'none' },
          borderColor: 'divider',
        }}>
        {rows.length > 0 && (
          <Button
            variant={isSmallScreen ? 'outlined' : 'text'}
            startIcon={<Add />}
            disabled={isLimitReached}
            onClick={addRow}
            sx={{ flex: { xs: 1, md: 'none' } }}>
            {t('extensionSettings.shortcuts.add')}
          </Button>
        )}
        <LoadingButton
          variant="contained"
          loading={isSaving}
          disabled={!hasChanges || hasErrors}
          onClick={save}
          sx={{ boxShadow: 'none', flex: { xs: 1, md: 'none' }, marginInlineStart: { md: 'auto' } }}>
          {t('save')}
        </LoadingButton>
      </Grid>
    </Grid>
  );
};
